import { useEffect, useRef } from 'react';
import { API } from '@unboared/react';

import { IClient, Mode } from '~/types/client';
import { normalizeScreenID } from '~/utils/screenID';
import { FreePlayIdleScreen } from '~/components/freePlay';

/** API calls throw outside an active session (gotcha #16) → best-effort wrap. */
function safe(fn: () => void) {
    try {
        fn();
    } catch {
        /* socket not ready yet — the next flag change re-applies */
    }
}

/**
 * Animator Control (V1.1) — applique en live les commandes à distance que
 * l'animateur écrit dans screenInfos.anim (pause / son / veille). Monté dans
 * ScreenApp SOUS <UnboaredGame> (l'API socket doit être initialisée) et seulement
 * sur les écrans anim/bar (clé partagée 'anim'). Reçoit le client déjà synchronisé
 * par ScreenApp (pas d'abonnement Firestore supplémentaire).
 *
 * pause/mute sont des ordres socket : chaque jeu doit écouter onPause/onMute pour
 * les honorer (point de vigilance transverse, cf brief). veille = overlay attract.
 */
export function AnimatorScreenControls({
    client,
    screenID,
    urlGameID,
}: {
    client: IClient;
    screenID?: string;
    /** Jeu réellement affiché (param d'URL de l'écran) — source de vérité pour
        restaurer les manettes en sortie de veille (Firestore peut être périmé). */
    urlGameID: string;
}) {
    const isAnim = normalizeScreenID(screenID) === Mode.Anim;
    const screen = isAnim ? client.getScreen(Mode.Anim) : undefined;
    const paused = screen?.paused ?? false;
    const muted = screen?.muted ?? false;
    const idle = screen?.idle ?? false;

    // La veille fige aussi le jeu sous-jacent : sinon il continue de tourner (et de
    // faire du SON) derrière l'overlay. On dérive donc l'état effectif du jeu de
    // `paused || idle` (et la sourdine de `muted || idle`) pour couvrir les jeux
    // qui n'écoutent qu'un seul des deux hooks.
    const shouldPause = paused || idle;
    const shouldMute = muted || idle;

    // Au montage on n'applique QUE l'état actif (true) — on ne force jamais
    // resume/unmute, ce qui écraserait l'état posé par le ControlPanel local.
    // Ensuite, on suit les deux sens.
    const firstPause = useRef(true);
    useEffect(() => {
        if (firstPause.current) {
            firstPause.current = false;
            if (shouldPause) safe(() => API.pause());
            return;
        }
        safe(() => (shouldPause ? API.pause() : API.resume()));
    }, [shouldPause]);

    const firstMute = useRef(true);
    useEffect(() => {
        if (firstMute.current) {
            firstMute.current = false;
            if (shouldMute) safe(() => API.mute(true));
            return;
        }
        safe(() => API.mute(shouldMute));
    }, [shouldMute]);

    // En veille, on diffuse gameID=null aux manettes pour qu'elles basculent sur
    // l'écran inter-jeux (« l'animateur prépare la partie »), comme pendant un
    // changement de jeu. À la sortie, on restaure le jeu de l'URL (et non la valeur
    // Firestore, qui peut être périmée → manettes sur le mauvais jeu / coincées).
    const gameRef = useRef(urlGameID);
    gameRef.current = urlGameID;
    const firstIdle = useRef(true);
    useEffect(() => {
        if (firstIdle.current) {
            firstIdle.current = false;
            if (!idle) return; // au montage hors veille : ne pas toucher au gameID partagé
        }
        safe(() => API.loadSharedProperty(API.getHostID(), 'gameID', idle ? null : gameRef.current));
    }, [idle]);

    if (!isAnim || !idle) return null;
    // Overlay plein écran au-dessus du jeu (l'idle est position:absolute inset:0).
    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50 }}>
            <FreePlayIdleScreen variant="anim" />
        </div>
    );
}
